import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import clsx from "clsx";
import { HeroInlineLogo } from "@/components/dataTable";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import { IndeterminateBar, Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getUserLocalLogin, useCurrentUser, userHasGroup } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { isPrivateTimeAccountLogin } from "@/lib/zeiterfassungPermissions";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import {
  ABSENCE_STATUS_LABELS,
  ABSENCE_TYPE_OPTIONS,
  availableOvertimeReductionDays,
  cancelAbsenceRequest,
  createAbsenceRequest,
  deleteAbsenceRequest,
  formatAbsenceDateRange,
  formatAbsenceDays,
  formatBudgetDays,
  formatDateDe,
  getAbsenceTypeLabel,
  isAutoApprovedWithoutReview,
  isPublicPendingAbsenceType,
  isUnavailabilityNotice,
  listAbsenceBudgets,
  listAbsenceRequests,
  MAX_VACATION_CALENDAR_SPAN_DAYS,
  maxBisDateForOvertimeReduction,
  maxBisDateForVacation,
  reservedOvertimeReductionDays,
  type EmployeeAbsenceBudget,
  type AbsenceType,
  type EmployeeAbsenceRequest,
  type HalfDayPart,
  type AbsenceStatus,
} from "@/lib/employeeAbsences";
import {
  calendarDayTitle,
  calendarDaysToMap,
  calendarKindLabel,
  deleteBridgeDayOverride,
  getCalendarDays,
  isAutoAbsenceCalendarDay,
  upsertBridgeDayOverride,
  type EmployeeCalendarDay,
} from "@/lib/employeeCalendar";
import {
  formatHeroAbsenceRange,
  getHeroEmployeeCalendar,
  type HeroEmployeeAbsence,
} from "@/lib/heroEmployeeHr";
import {
  getEmployeeAccountSummary,
  type EmployeeAccountSummary,
} from "@/lib/employeeTimes";
import { AbsenceBudgetOverview } from "@/pages/admin/zeiterfassung/AbsenceBudgetOverview";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";

interface AbsenceRequestPanelProps {
  refreshKey: number;
  defaultEmployeeLogin: string;
  defaultEmployeeDisplay: string;
  allowEmployeePicker?: boolean;
  /** Büro/GF: genehmigte App-Abwesenheiten mit Begründung löschen */
  allowOfficeDelete?: boolean;
  includeHeroOverlay?: boolean;
  onChanged?: () => void;
}

type DurationMode = "full" | "half_am" | "half_pm";

/** Jahre, deren Urlaubskontingent ein Antrag ab Von berühren kann (inkl. 3-Wochen-Deckel). */
function vacationQuotaYears(dateFrom: string, dateTo: string): number[] {
  const from = dateFrom.slice(0, 10);
  const fromYear = Number(from.slice(0, 4));
  const toYear = dateTo ? Number(dateTo.slice(0, 4)) : fromYear;
  const cap = new Date(`${from}T00:00:00Z`);
  cap.setUTCDate(cap.getUTCDate() + MAX_VACATION_CALENDAR_SPAN_DAYS);
  return [...new Set([fromYear, toYear, cap.getUTCFullYear()])].filter(
    (year) => Number.isInteger(year) && year >= 2000,
  );
}

function durationToPayload(
  dateFrom: string,
  dateTo: string,
  mode: DurationMode,
): { day_fraction: number; half_day_part: HalfDayPart | null } {
  if (dateFrom !== dateTo || mode === "full") {
    return { day_fraction: 1, half_day_part: null };
  }
  if (mode === "half_am") return { day_fraction: 0.5, half_day_part: "am" };
  return { day_fraction: 0.5, half_day_part: "pm" };
}

function formatReviewedAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

type AbsenceListFilter = AbsenceStatus | "all" | "active";

const ABSENCE_LIST_FILTER_OPTIONS: Array<{ value: AbsenceListFilter; label: string }> = [
  { value: "active", label: "Aktiv" },
  { value: "all", label: "Alle" },
  { value: "pending", label: "Offen" },
  { value: "approved", label: ABSENCE_STATUS_LABELS.approved },
  { value: "rejected", label: ABSENCE_STATUS_LABELS.rejected },
  { value: "cancelled", label: ABSENCE_STATUS_LABELS.cancelled },
];

type SourceFilter = "all" | "app" | "hero";

const MONTH_LABEL = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split("-");
  const y = Number(year);
  const m = Number(month);
  if (!y || !m) return yearMonth;
  return MONTH_LABEL.format(new Date(y, m - 1, 1));
}

function requestCoversDate(req: EmployeeAbsenceRequest, iso: string): boolean {
  if (req.status !== "pending" && req.status !== "approved" && req.status !== "acknowledged") {
    return false;
  }
  const day = iso.slice(0, 10);
  return req.date_from.slice(0, 10) <= day && req.date_to.slice(0, 10) >= day;
}

function heroMatchesFilter(status: AbsenceStatus, filter: AbsenceListFilter): boolean {
  if (filter === "all") return true;
  if (filter === "active") return status === "pending" || status === "approved";
  return status === filter;
}

function statusAccentClass(status: string): string {
  switch (status) {
    case "pending":
      return "border-l-status-orange";
    case "approved":
    case "acknowledged":
      return "border-l-status-green";
    case "rejected":
      return "border-l-status-red";
    default:
      return "border-l-separator";
  }
}

type TimelineItem =
  | { key: string; sort: string; month: string; kind: "request"; req: EmployeeAbsenceRequest }
  | { key: string; sort: string; month: string; kind: "auto"; day: EmployeeCalendarDay }
  | { key: string; sort: string; month: string; kind: "hero"; absence: HeroEmployeeAbsence };

function listEmptyText(filter: AbsenceListFilter, year: number, source: SourceFilter): string {
  if (source === "hero") return `Keine Hero-Abwesenheiten in ${year}.`;
  if (filter === "active") return `Keine offenen oder genehmigten Abwesenheiten in ${year}.`;
  return `Keine Einträge für diesen Filter in ${year}.`;
}

function RequestRow({
  req,
  onCancel,
  onOfficeDelete,
  allowCancelApprovedOvertime = false,
}: {
  req: EmployeeAbsenceRequest;
  onCancel?: (id: number) => void;
  onOfficeDelete?: (req: EmployeeAbsenceRequest) => void;
  /** Sonderregelung: genehmigten Überstundenabbau selbst zurückziehen */
  allowCancelApprovedOvertime?: boolean;
}) {
  const autoApprovedWithoutReview = isAutoApprovedWithoutReview(req);
  const canCancel =
    Boolean(onCancel) &&
    (req.status === "pending" ||
      (isUnavailabilityNotice(req.absence_type) && req.status === "acknowledged") ||
      (allowCancelApprovedOvertime &&
        req.status === "approved" &&
        req.absence_type === "ueberstundenabbau"));

  return (
    <div
      className={clsx(
        "flex flex-wrap items-start justify-between gap-3 rounded-xl border border-separator border-l-4 bg-card px-3 py-2.5",
        statusAccentClass(req.status),
      )}
    >
      <div>
        <div className="font-medium text-label">
          {getAbsenceTypeLabel(req.absence_type)}
          <span className="ml-2 text-sm font-normal text-label-secondary">
            · {formatAbsenceDays(req.total_days)}
          </span>
        </div>
        <div className="text-sm text-label-secondary">{formatAbsenceDateRange(req)}</div>
        {req.note && <div className="mt-1 text-sm text-label-secondary">{req.note}</div>}
        {req.status === "approved" &&
          !autoApprovedWithoutReview &&
          (req.reviewed_by_display || req.reviewed_at) && (
          <div className="mt-1 text-xs text-label-tertiary">
            Freigegeben
            {req.reviewed_by_display ? ` von ${req.reviewed_by_display}` : ""}
            {req.reviewed_at ? ` am ${formatReviewedAt(req.reviewed_at)}` : ""}
          </div>
        )}
        {autoApprovedWithoutReview && (
          <div className="mt-1 text-xs text-label-tertiary">
            Sofort wirksam (keine Freigabe erforderlich)
          </div>
        )}
        {req.status === "rejected" && req.review_comment && (
          <div className="mt-1 text-sm text-status-red">Ablehnung: {req.review_comment}</div>
        )}
        {req.status === "cancelled" && req.review_comment && (
          <div className="mt-1 text-xs text-label-tertiary">{req.review_comment}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(req.status)}`}
        >
          {absenceStatusLabel(req)}
        </span>
        {canCancel && onCancel && (
          <Button variant="secondary" size="sm" onClick={() => onCancel(req.id)}>
            Zurückziehen
          </Button>
        )}
        {(req.status === "approved" ||
          req.status === "pending" ||
          req.status === "rejected" ||
          req.status === "acknowledged") &&
          onOfficeDelete && (
            <Button variant="danger" size="sm" onClick={() => onOfficeDelete(req)}>
              Löschen
            </Button>
          )}
      </div>
    </div>
  );
}

function absenceStatusLabel(req: EmployeeAbsenceRequest): string {
  if (isUnavailabilityNotice(req.absence_type)) {
    if (req.status === "pending") return "Hinweis";
    if (req.status === "acknowledged") return "Gelesen";
  }
  return ABSENCE_STATUS_LABELS[req.status];
}

function statusBadgeClass(status: EmployeeAbsenceRequest["status"]): string {
  switch (status) {
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "approved":
      return "bg-status-green/15 text-status-green";
    case "rejected":
      return "bg-status-red/15 text-status-red";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}

export function AbsenceRequestPanel({
  refreshKey,
  defaultEmployeeLogin,
  defaultEmployeeDisplay,
  allowEmployeePicker = false,
  allowOfficeDelete = false,
  includeHeroOverlay = false,
  onChanged,
}: AbsenceRequestPanelProps) {
  const { user } = useCurrentUser();
  const now = new Date();
  const [selectedEmployee, setSelectedEmployee] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
  );
  const employeeLogin = selectedEmployee?.value.login ?? "";
  const employeeDisplay = selectedEmployee?.value.display ?? "";
  const viewerLogin = getUserLocalLogin(user);
  const canCancelOwnAutoApprovedOvertime =
    isPrivateTimeAccountLogin(employeeLogin) && viewerLogin === employeeLogin;
  const [year, setYear] = useState(now.getFullYear());
  const [listFilter, setListFilter] = useState<AbsenceListFilter>("active");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [requests, setRequests] = useState<EmployeeAbsenceRequest[]>([]);
  const [budget, setBudget] = useState<EmployeeAbsenceBudget | null>(null);
  const [accountSummary, setAccountSummary] = useState<EmployeeAccountSummary | null>(null);
  const viewerIsNachunternehmer = userHasGroup(user, "Nachunternehmer");
  const noticeOnly =
    Boolean(selectedEmployee?.value.isNachunternehmer) ||
    accountSummary?.tracks_overtime === false ||
    (viewerIsNachunternehmer && (!allowEmployeePicker || viewerLogin === employeeLogin));
  const [calendarByDay, setCalendarByDay] = useState<Map<string, EmployeeCalendarDay>>(new Map());
  const [bridgeDefaultType, setBridgeDefaultType] = useState<AbsenceType>("urlaub");
  const [heroAbsences, setHeroAbsences] = useState<HeroEmployeeAbsence[]>([]);
  const [heroLinked, setHeroLinked] = useState(false);
  const [heroPartnerName, setHeroPartnerName] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [budgetLoading, setBudgetLoading] = useState(true);
  const [heroLoading, setHeroLoading] = useState(false);
  const loadSeq = useRef(0);
  const [saving, setSaving] = useState(false);

  const [absenceType, setAbsenceType] = useState<AbsenceType>("urlaub");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vacationAvailableByYear, setVacationAvailableByYear] = useState<Record<number, number>>(
    {},
  );
  const [extraHolidayDates, setExtraHolidayDates] = useState<ReadonlySet<string>>(new Set());
  const [durationMode, setDurationMode] = useState<DurationMode>("full");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (noticeOnly) {
      setAbsenceType("nicht_verfuegbar");
      setDurationMode("full");
      return;
    }
    setAbsenceType((current) => (current === "nicht_verfuegbar" ? "urlaub" : current));
  }, [noticeOnly]);

  const [deleteTarget, setDeleteTarget] = useState<EmployeeAbsenceRequest | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isSingleDay = Boolean(dateFrom && dateTo && dateFrom === dateTo);

  useEffect(() => {
    if (!allowEmployeePicker && defaultEmployeeLogin) {
      setSelectedEmployee(
        employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
      );
    }
  }, [allowEmployeePicker, defaultEmployeeLogin, defaultEmployeeDisplay]);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    const seq = ++loadSeq.current;
    if (!employeeLogin) {
      setRequests([]);
      setBudget(null);
      setAccountSummary(null);
      setCalendarByDay(new Map());
      setHeroAbsences([]);
      setHeroLinked(false);
      setHeroPartnerName(null);
      setListLoading(false);
      setBudgetLoading(false);
      setHeroLoading(false);
      return;
    }
    if (!options?.silent) {
      setListLoading(true);
      setBudgetLoading(true);
      setHeroLoading(includeHeroOverlay);
      setBudget(null);
      setHeroAbsences([]);
      setHeroLinked(false);
      setHeroPartnerName(null);
    }

    const rowsPromise = listAbsenceRequests({ user_login: employeeLogin, year });
    const calendarPromise = getCalendarDays({ user_login: employeeLogin, year });
    const budgetsPromise = listAbsenceBudgets({ user_login: employeeLogin, year });
    const summaryPromise = getEmployeeAccountSummary({
      user_login: employeeLogin,
      year,
      include_hero_balance: false,
    }).catch(() => null);
    const heroPromise = includeHeroOverlay
      ? getHeroEmployeeCalendar({
          year,
          user_login: employeeLogin,
          includeHours: false,
        }).catch(() => null)
      : Promise.resolve(null);

    try {
      const [rows, calendar] = await Promise.all([rowsPromise, calendarPromise]);
      if (seq !== loadSeq.current) return;
      setRequests(rows);
      setCalendarByDay(calendarDaysToMap(calendar.days));
      setBridgeDefaultType(calendar.bridge_default_absence_type);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      toast.error(err instanceof ApiError ? err.message : "Anträge konnten nicht geladen werden.");
    } finally {
      if (seq === loadSeq.current) setListLoading(false);
    }

    try {
      const [budgets, summary] = await Promise.all([budgetsPromise, summaryPromise]);
      if (seq !== loadSeq.current) return;
      setBudget(budgets[0] ?? null);
      setAccountSummary(summary);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      toast.error(err instanceof ApiError ? err.message : "Urlaubsbudget konnte nicht geladen werden.");
    } finally {
      if (seq === loadSeq.current) setBudgetLoading(false);
    }

    const heroData = await heroPromise;
    if (seq !== loadSeq.current) return;
    if (heroData) {
      setHeroAbsences(heroData.absences);
      setHeroLinked(heroData.linked);
      setHeroPartnerName(heroData.partner_name);
    } else if (includeHeroOverlay) {
      setHeroAbsences([]);
      setHeroLinked(false);
      setHeroPartnerName(null);
    }
    setHeroLoading(false);
  }, [employeeLogin, includeHeroOverlay, year]);

  useRefreshKeyLoad(load, refreshKey);

  useEffect(() => {
    setVacationAvailableByYear({});
    setExtraHolidayDates(new Set());
  }, [employeeLogin]);

  const panelAvailableVacationDays = Number(
    accountSummary?.available_vacation_days ?? budget?.available_vacation_days ?? 0,
  );
  const availableByYear = useMemo(() => {
    const panelDays = Number.isFinite(panelAvailableVacationDays) ? panelAvailableVacationDays : 0;
    return { ...vacationAvailableByYear, [year]: panelDays };
  }, [panelAvailableVacationDays, vacationAvailableByYear, year]);

  useEffect(() => {
    if (!employeeLogin || absenceType !== "urlaub" || !dateFrom) return;
    const needed = vacationQuotaYears(dateFrom, dateTo).filter(
      (quotaYear) => availableByYear[quotaYear] === undefined,
    );
    if (needed.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await Promise.all(
          needed.map(async (quotaYear) => {
            const [budgets, calendar] = await Promise.all([
              listAbsenceBudgets({ user_login: employeeLogin, year: quotaYear }),
              getCalendarDays({ user_login: employeeLogin, year: quotaYear }),
            ]);
            const available = Number(budgets[0]?.available_vacation_days ?? 0);
            return {
              quotaYear,
              available: Number.isFinite(available) ? available : 0,
              holidays: calendar.days
                .filter((day) => day.kind === "holiday")
                .map((day) => day.work_date.slice(0, 10)),
            };
          }),
        );
        if (cancelled) return;
        setVacationAvailableByYear((prev) => {
          const next = { ...prev };
          for (const row of rows) next[row.quotaYear] = row.available;
          return next;
        });
        setExtraHolidayDates((prev) => {
          const next = new Set(prev);
          for (const row of rows) {
            for (const iso of row.holidays) next.add(iso);
          }
          return next;
        });
      } catch {
        // Das Backend prüft das Kontingent beim Speichern erneut.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [absenceType, availableByYear, dateFrom, dateTo, employeeLogin]);

  const holidayDates = useMemo(() => {
    const set = new Set<string>(extraHolidayDates);
    for (const day of calendarByDay.values()) {
      if (day.kind === "holiday") set.add(day.work_date.slice(0, 10));
    }
    return set;
  }, [calendarByDay, extraHolidayDates]);

  const requestYear = dateFrom ? Number(dateFrom.slice(0, 4)) : year;
  const requestYearQuota = availableByYear[requestYear];
  const requestQuotaKnown = requestYearQuota !== undefined;
  const availableVacationDays = requestYearQuota ?? Number.NaN;

  // Arbeitstage laut Arbeitszeitmodell (z. B. 4-Tage-Woche): freie Wochentage
  // verbrauchen kein Kontingent und verlängern den erlaubten Zeitraum entsprechend.
  const workWeekdays = useMemo<ReadonlySet<number> | null>(() => {
    const raw = accountSummary?.work_weekdays;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return new Set(raw.map((v) => Number(v)).filter((v) => v >= 1 && v <= 7));
  }, [accountSummary?.work_weekdays]);

  const overtimeAvailableDays = useMemo(() => {
    const reserved = reservedOvertimeReductionDays(
      requests,
      accountSummary?.overtime_as_of,
    );
    return availableOvertimeReductionDays({
      overtimeBalanceHours: accountSummary?.overtime_balance_hours,
      averageDailySollHours: accountSummary?.average_daily_soll_hours,
      reservedDays: reserved,
    });
  }, [accountSummary, requests]);

  const maxDateTo = useMemo(() => {
    if (!dateFrom) return null;
    if (absenceType === "urlaub") {
      return maxBisDateForVacation({
        dateFrom,
        availableVacationDays: requestQuotaKnown ? availableVacationDays : 0,
        availableVacationDaysByYear: availableByYear,
        holidayDates,
        workWeekdays,
      });
    }
    if (absenceType === "ueberstundenabbau") {
      return maxBisDateForOvertimeReduction({
        dateFrom,
        availableDays: overtimeAvailableDays,
        holidayDates,
        workWeekdays,
      });
    }
    return null;
  }, [
    absenceType,
    availableByYear,
    availableVacationDays,
    dateFrom,
    holidayDates,
    overtimeAvailableDays,
    requestQuotaKnown,
    workWeekdays,
  ]);

  useEffect(() => {
    if (dateFrom && !dateTo) {
      setDateTo(dateFrom);
      return;
    }
    if (!dateFrom || !dateTo) return;
    if (dateTo < dateFrom) {
      setDateTo(dateFrom);
      return;
    }
    if (maxDateTo && dateTo > maxDateTo) {
      setDateTo(maxDateTo);
    }
  }, [dateFrom, dateTo, maxDateTo]);

  useEffect(() => {
    if (!isSingleDay && durationMode !== "full") {
      setDurationMode("full");
    }
  }, [isSingleDay, durationMode]);

  const rangeLimitHint = useMemo(() => {
    if (absenceType === "urlaub") {
      if (!requestQuotaKnown) {
        return "Bis maximal 3 Wochen. Das Urlaubskontingent des gewählten Jahres wird geladen.";
      }
      if (!Number.isFinite(availableVacationDays) || availableVacationDays <= 0) {
        return `Kein Resturlaub in ${requestYear} verfügbar – Urlaub kann nicht beantragt werden.`;
      }
      const toYear = dateTo ? Number(dateTo.slice(0, 4)) : requestYear;
      const toYearQuota = availableByYear[toYear];
      if (toYear !== requestYear && toYearQuota !== undefined) {
        return `Bis maximal 3 Wochen. Verfügbar: ${formatBudgetDays(availableVacationDays)} Tage in ${requestYear} und ${formatBudgetDays(toYearQuota)} Tage in ${toYear}. Halbe Tage: Von und Bis auf denselben Tag setzen.`;
      }
      const yearNote = requestYear !== year ? ` in ${requestYear}` : "";
      return `Bis maximal 3 Wochen bzw. ${formatBudgetDays(availableVacationDays)} verfügbare Urlaubstage${yearNote}. Halbe Tage: Von und Bis auf denselben Tag setzen.`;
    }
    if (absenceType === "ueberstundenabbau") {
      if (overtimeAvailableDays <= 0) {
        return "Kein Überstundenabbau verfügbar – auf dem Konto sind keine Tage frei.";
      }
      return `Maximal ${formatBudgetDays(overtimeAvailableDays)} Tage Überstundenabbau laut aktuellem Kontostand. Halbe Tage: Von und Bis auf denselben Tag setzen.`;
    }
    return null;
  }, [
    absenceType,
    availableByYear,
    availableVacationDays,
    dateTo,
    overtimeAvailableDays,
    requestQuotaKnown,
    requestYear,
    year,
  ]);

  const selectedCalendarDay =
    isSingleDay && dateFrom ? calendarByDay.get(dateFrom) ?? null : null;

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      if (listFilter === "all") return true;
      if (listFilter === "active") {
        return (
          req.status === "pending" ||
          req.status === "approved" ||
          (isUnavailabilityNotice(req.absence_type) && req.status === "acknowledged")
        );
      }
      return req.status === listFilter;
    });
  }, [requests, listFilter]);

  const timeline = useMemo(() => {
    const items: TimelineItem[] = [];
    const showApp = sourceFilter !== "hero";
    const showHero = includeHeroOverlay && sourceFilter !== "app";
    const showAuto =
      listFilter === "all" || listFilter === "active" || listFilter === "approved";

    if (showApp) {
      for (const req of filteredRequests) {
        const sort = req.date_from.slice(0, 10);
        items.push({
          key: `app-${req.id}`,
          sort,
          month: sort.slice(0, 7),
          kind: "request",
          req,
        });
      }
      if (showAuto) {
        const autoDays = [...calendarByDay.values()]
          .filter(
            (day) =>
              isAutoAbsenceCalendarDay(day) &&
              !day.works_instead &&
              day.work_date.startsWith(String(year)),
          )
          .sort((a, b) => a.work_date.localeCompare(b.work_date));
        for (const day of autoDays) {
          const iso = day.work_date.slice(0, 10);
          if (requests.some((req) => requestCoversDate(req, iso))) continue;
          items.push({
            key: `auto-${day.kind}-${iso}`,
            sort: iso,
            month: iso.slice(0, 7),
            kind: "auto",
            day,
          });
        }
      }
    }

    if (showHero) {
      for (const absence of heroAbsences) {
        if (!heroMatchesFilter(absence.status, listFilter)) continue;
        const sort = absence.start.slice(0, 10);
        items.push({
          key: `hero-${absence.id ?? sort}-${absence.end}-${absence.absence_type}`,
          sort,
          month: sort.slice(0, 7),
          kind: "hero",
          absence,
        });
      }
    }

    items.sort((a, b) => a.sort.localeCompare(b.sort) || a.key.localeCompare(b.key));
    return items;
  }, [
    calendarByDay,
    filteredRequests,
    heroAbsences,
    includeHeroOverlay,
    listFilter,
    requests,
    sourceFilter,
    year,
  ]);

  const timelineGroups = useMemo(() => {
    const groups: { month: string; label: string; items: TimelineItem[] }[] = [];
    for (const item of timeline) {
      const last = groups[groups.length - 1];
      if (!last || last.month !== item.month) {
        groups.push({ month: item.month, label: formatMonthLabel(item.month), items: [item] });
      } else {
        last.items.push(item);
      }
    }
    return groups;
  }, [timeline]);

  const handleWorkOnAutoAbsenceDay = async (day: EmployeeCalendarDay) => {
    const workDate = day.work_date.slice(0, 10);
    const kindLabel = calendarKindLabel(day.kind);
    try {
      await upsertBridgeDayOverride({
        work_date: workDate,
        absence_type: null,
        user_login: employeeLogin,
        note: `An ${kindLabel}-Tag arbeiten`,
      });
      toast.success(`${kindLabel}: Override gespeichert – Sie planen zu arbeiten.`);
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Override fehlgeschlagen.");
    }
  };

  const handleRequestAutoAbsenceAlternative = (day: EmployeeCalendarDay) => {
    setDateFrom(day.work_date.slice(0, 10));
    setDateTo(day.work_date.slice(0, 10));
    setAbsenceType("ueberstundenabbau");
    if ((day.day_fraction ?? 1) < 1 && day.half_day_part === "am") {
      setDurationMode("half_am");
    } else if ((day.day_fraction ?? 1) < 1) {
      setDurationMode("half_pm");
    } else {
      setDurationMode("full");
    }
    setNote("");
  };

  const handleClearBridgeOverride = async (workDate: string) => {
    try {
      await deleteBridgeDayOverride({ work_date: workDate, user_login: employeeLogin });
      await load({ silent: true });
    } catch {
      /* override may not exist */
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!employeeLogin || !dateFrom || !dateTo) {
      toast.error("Bitte Mitarbeiter und Zeitraum ausfüllen.");
      return;
    }
    if (dateTo < dateFrom) {
      toast.error("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }
    if (!noticeOnly && absenceType === "urlaub") {
      if (requestQuotaKnown && (!Number.isFinite(availableVacationDays) || availableVacationDays <= 0)) {
        toast.error(`Kein Resturlaub in ${requestYear} verfügbar.`);
        return;
      }
      if (maxDateTo && dateTo > maxDateTo) {
        toast.error(
          `Urlaub darf maximal bis ${maxDateTo.split("-").reverse().join(".")} beantragt werden (3 Wochen oder Restanspruch).`,
        );
        return;
      }
      if (
        durationMode !== "full" &&
        requestQuotaKnown &&
        availableVacationDays < 0.5
      ) {
        toast.error(`Für einen halben Urlaubstag ist in ${requestYear} nicht genug Resturlaub vorhanden.`);
        return;
      }
    }
    if (!noticeOnly && absenceType === "ueberstundenabbau") {
      if (overtimeAvailableDays <= 0) {
        toast.error("Kein Überstundenabbau verfügbar.");
        return;
      }
      if (maxDateTo && dateTo > maxDateTo) {
        toast.error(
          `Überstundenabbau darf maximal bis ${maxDateTo.split("-").reverse().join(".")} beantragt werden.`,
        );
        return;
      }
      if (durationMode !== "full" && overtimeAvailableDays < 0.5) {
        toast.error("Für einen halben Tag Überstundenabbau reicht der Kontostand nicht.");
        return;
      }
    }

    setSaving(true);
    try {
      const duration = noticeOnly
        ? { day_fraction: 1 as const, half_day_part: null }
        : durationToPayload(dateFrom, dateTo, durationMode);
      const submitType = noticeOnly ? "nicht_verfuegbar" : absenceType;
      const created = await createAbsenceRequest({
        absence_type: submitType,
        date_from: dateFrom,
        date_to: dateTo,
        ...duration,
        note: note.trim() || undefined,
        user_login: employeeLogin,
        user_display: employeeDisplay,
      });
      if (!noticeOnly && isAutoAbsenceCalendarDay(selectedCalendarDay)) {
        await handleClearBridgeOverride(dateFrom);
      }
      if (noticeOnly) {
        toast.success("Nicht verfügbar ist im Montageplan eingetragen.");
      } else if (created.status === "approved") {
        toast.success(
          absenceType === "ueberstundenabbau"
            ? "Überstundenabbau wurde eingetragen (keine Freigabe erforderlich)."
            : "Abwesenheit wurde genehmigt.",
        );
      } else if (isPublicPendingAbsenceType(absenceType)) {
        toast.success(
          "Krankheitstage sind beantragt und im Montageplan sichtbar (warten auf Freigabe).",
        );
      } else {
        toast.success("Abwesenheitsantrag wurde eingereicht und wartet auf Freigabe.");
      }
      setDateFrom("");
      setDateTo("");
      setDurationMode("full");
      setNote("");
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Antrag konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await cancelAbsenceRequest(id);
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Zurückziehen fehlgeschlagen.");
    }
  };

  const closeOfficeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteReason("");
  };

  const confirmOfficeDelete = async () => {
    if (!deleteTarget) return;
    const reason = deleteReason.trim();
    if (reason.length < 3) {
      toast.error("Bitte eine Begründung mit mindestens 3 Zeichen angeben.");
      return;
    }
    setDeleting(true);
    try {
      await deleteAbsenceRequest(deleteTarget.id, reason);
      toast.success("Abwesenheit wurde gelöscht (Begründung gespeichert).");
      setDeleteTarget(null);
      setDeleteReason("");
      onChanged?.();
      await load({ silent: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  };

  const officeDeleteHandler = allowOfficeDelete
    ? (req: EmployeeAbsenceRequest) => {
        setDeleteTarget(req);
        setDeleteReason("");
      }
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-label">Abwesenheiten</h2>
          <p className="truncate text-sm text-label-secondary">
            {employeeDisplay || employeeLogin || "Mitarbeiter wählen"}
            {includeHeroOverlay && heroLinked && heroPartnerName ? ` · Hero: ${heroPartnerName}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {allowEmployeePicker ? (
            <div className="min-w-[16rem] flex-1">
              <EmployeeLockedAutocomplete
                value={selectedEmployee}
                onChange={setSelectedEmployee}
              />
            </div>
          ) : null}
          <label className="space-y-1">
            <span className="eh-label">Jahr</span>
            <select
              className="eh-input"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {!noticeOnly && (
        <AbsenceBudgetOverview
          year={year}
          budget={budget}
          heroLinked={heroLinked}
          loading={budgetLoading}
        />
      )}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)]">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="order-2 h-fit space-y-4 rounded-ios border border-separator bg-card p-4 shadow-ios xl:order-1 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto"
      >
        <div>
          <h2 className="text-base font-semibold text-label">
            {noticeOnly ? "Nicht verfügbar eintragen" : "Neuer Antrag"}
          </h2>
          <p className="mt-1 text-sm text-label-secondary">
            {noticeOnly
              ? "Erscheint im Montageplan. Die Geschäftsführung sieht den Eintrag als Hinweis."
              : isPublicPendingAbsenceType(absenceType)
                ? "Krankheit ist sofort im Montageplan sichtbar. Die Freigabe bleibt nötig."
                : "Geht an die Geschäftsführung zur Freigabe."}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {!noticeOnly && (
          <div className="space-y-1.5">
            <span className="eh-label">Art</span>
            <div
              className="grid grid-cols-1 gap-1.5"
              role="radiogroup"
              aria-label="Art der Abwesenheit"
              data-tour="abwesenheit-art"
            >
              {ABSENCE_TYPE_OPTIONS.map((opt) => {
                const active = absenceType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={clsx(
                      "rounded-lg border px-3 py-2 text-left text-sm font-medium leading-snug transition",
                      active
                        ? "border-accent bg-accent text-label-on-accent shadow-sm"
                        : "border-separator bg-card text-label hover:border-accent/60 active:bg-fill-tertiary",
                    )}
                    onClick={() => setAbsenceType(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1" data-tour="abwesenheit-von">
              <span className="eh-label">Von</span>
              <input
                type="date"
                className="eh-input h-12 w-full text-base md:h-auto md:text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                required
              />
            </label>

            <label className="space-y-1" data-tour="abwesenheit-bis">
              <span className="eh-label">Bis</span>
              <input
                type="date"
                className="eh-input h-12 w-full text-base md:h-auto md:text-sm"
                value={dateTo}
                min={dateFrom || undefined}
                max={maxDateTo || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                required
              />
            </label>
          </div>

          {!noticeOnly && rangeLimitHint && (
            <p className="text-xs text-label-secondary md:col-span-2">{rangeLimitHint}</p>
          )}

          {!noticeOnly && (
          <div className="space-y-1 md:col-span-2">
            <span className="eh-label">Umfang</span>
            <SegmentedControl
              value={isSingleDay ? durationMode : "full"}
              onChange={setDurationMode}
              name="Umfang"
              size="md"
              disabled={!isSingleDay}
              className="flex w-full md:inline-flex md:w-auto"
              options={[
                { value: "full", label: "Ganzer Tag" },
                { value: "half_am", label: "Vormittag" },
                { value: "half_pm", label: "Nachmittag" },
              ]}
            />
            {!isSingleDay && (
              <p className="text-xs text-label-secondary">
                Vormittag oder Nachmittag nur bei einem einzelnen Tag (Von und Bis gleich).
              </p>
            )}
          </div>
          )}

          {!noticeOnly && selectedCalendarDay && (
            <div className="md:col-span-2">
              <Alert
                variant={selectedCalendarDay.kind === "holiday" ? "warning" : "info"}
                title={
                  selectedCalendarDay.kind === "holiday"
                    ? `Feiertag: ${selectedCalendarDay.label}`
                    : `${calendarKindLabel(selectedCalendarDay.kind)}: ${selectedCalendarDay.label}`
                }
              >
                {isAutoAbsenceCalendarDay(selectedCalendarDay) && (
                  <p className="text-sm">
                    {selectedCalendarDay.is_bridge_default ? (
                      <>
                        Standardmäßig ist{" "}
                        <strong>
                          {getAbsenceTypeLabel(
                            selectedCalendarDay.effective_absence_type ??
                              (selectedCalendarDay.kind === "betriebsferien"
                                ? "urlaub"
                                : bridgeDefaultType),
                          )}
                        </strong>{" "}
                        bereits automatisch genehmigt eingetragen
                        {(selectedCalendarDay.day_fraction ?? 1) < 1
                          ? " (½ Tag, Nachmittag)."
                          : "."}
                      </>
                    ) : selectedCalendarDay.works_instead ? (
                      <>
                        Für diesen {calendarKindLabel(selectedCalendarDay.kind)}-Tag ist Arbeit
                        geplant.
                      </>
                    ) : (
                      <>
                        Abweichende Abwesenheit:{" "}
                        <strong>
                          {selectedCalendarDay.effective_absence_type
                            ? getAbsenceTypeLabel(selectedCalendarDay.effective_absence_type)
                            : "—"}
                        </strong>
                        {selectedCalendarDay.absence_request_status === "pending" && " (Antrag offen)"}
                      </>
                    )}
                  </p>
                )}
                {isAutoAbsenceCalendarDay(selectedCalendarDay) &&
                  !selectedCalendarDay.works_instead && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRequestAutoAbsenceAlternative(selectedCalendarDay)}
                    >
                      Abweichend beantragen
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void handleWorkOnAutoAbsenceDay(selectedCalendarDay)}
                    >
                      An diesem Tag arbeiten
                    </Button>
                  </div>
                )}
                {isAutoAbsenceCalendarDay(selectedCalendarDay) && (
                  <p className="mt-2 text-xs text-label-secondary">
                    Ein abweichender Antrag (z. B. Überstundenabbau) ersetzt den automatischen Urlaub
                    nach Freigabe. „An diesem Tag arbeiten“ trägt keinen Urlaub ein.
                    {selectedCalendarDay.kind === "betriebsferien" &&
                      " An Heiligabend und Silvester zählt nur ein halber Urlaubstag."}
                  </p>
                )}
              </Alert>
            </div>
          )}

          <div className="md:col-span-2">
            <TextInput
              label={noticeOnly ? "Text für den Montageplan (optional)" : "Anmerkung (optional)"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                noticeOnly ? "z. B. andere Baustelle, nicht im Haus" : "z. B. Vertretung, Arztbesuch …"
              }
            />
          </div>
        </div>

        <Button
          type="submit"
          data-tour="abwesenheit-einreichen"
          variant="primary"
          size="lg"
          className="w-full md:w-auto"
          disabled={saving || !employeeLogin}
        >
          {saving ? "Wird eingereicht…" : noticeOnly ? "Eintragen" : "Antrag einreichen"}
        </Button>
      </form>

      <section className="order-1 min-w-0 rounded-ios border border-separator bg-card p-4 shadow-ios xl:order-2">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-label">
            Jahresverlauf
            <span className="ml-2 font-medium text-label-tertiary">{timeline.length}</span>
          </h3>
          {heroLoading ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-label-tertiary">
              <Spinner />
              Hero
            </span>
          ) : null}
        </div>

        <div className="mb-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Statusfilter">
          {ABSENCE_LIST_FILTER_OPTIONS.map((opt) => {
            const active = listFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={clsx(
                  "rounded-full px-3 py-1 text-xs font-medium transition",
                  active
                    ? "bg-accent text-label-on-accent"
                    : "bg-fill-tertiary text-label-secondary hover:text-label",
                )}
                onClick={() => setListFilter(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {includeHeroOverlay ? (
          <div className="mb-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Quelle">
            {(
              [
                { value: "all", label: "Gesamt" },
                { value: "app", label: "App" },
                { value: "hero", label: "Hero" },
              ] as const
            ).map((opt) => {
              const active = sourceFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={clsx(
                    "rounded-full border px-3 py-1 text-xs font-medium transition",
                    active
                      ? "border-violet-500/40 bg-violet-500/10 text-label"
                      : "border-transparent bg-fill-quaternary text-label-tertiary hover:text-label",
                  )}
                  onClick={() => setSourceFilter(opt.value)}
                >
                  {opt.value === "hero" ? (
                    <span className="inline-flex items-center gap-1">
                      <HeroInlineLogo />
                      {opt.label}
                    </span>
                  ) : (
                    opt.label
                  )}
                </button>
              );
            })}
          </div>
        ) : null}

        {heroLoading && !listLoading ? (
          <IndeterminateBar className="mb-3" label="Hero-Abwesenheiten werden geladen" />
        ) : null}

        {listLoading ? (
          <div className="space-y-2" aria-hidden>
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="h-16 animate-pulse rounded-xl bg-fill-tertiary/70" />
            ))}
          </div>
        ) : timeline.length === 0 ? (
          <p className="py-6 text-sm text-label-secondary">
            {heroLoading
              ? "App-Daten sind da. Hero-Einträge werden noch geladen."
              : listEmptyText(listFilter, year, sourceFilter)}
          </p>
        ) : (
          <div className="space-y-5">
            {timelineGroups.map((group) => (
              <section key={group.month}>
                <h4 className="mb-2 flex items-baseline justify-between text-sm font-semibold text-label">
                  <span>{group.label}</span>
                  <span className="text-xs font-medium text-label-tertiary">{group.items.length}</span>
                </h4>
                <div className="space-y-2">
                  {group.items.map((item) => {
                    if (item.kind === "request") {
                      return (
                        <RequestRow
                          key={item.key}
                          req={item.req}
                          onCancel={
                            item.req.status === "pending" ||
                            (canCancelOwnAutoApprovedOvertime &&
                              item.req.status === "approved" &&
                              item.req.absence_type === "ueberstundenabbau") ||
                            (isUnavailabilityNotice(item.req.absence_type) &&
                              item.req.status === "acknowledged")
                              ? (id) => void handleCancel(id)
                              : undefined
                          }
                          allowCancelApprovedOvertime={canCancelOwnAutoApprovedOvertime}
                          onOfficeDelete={officeDeleteHandler}
                        />
                      );
                    }
                    if (item.kind === "auto") {
                      const day = item.day;
                      const fraction = day.day_fraction ?? 1;
                      return (
                        <div
                          key={item.key}
                          className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-separator border-l-4 border-l-accent bg-card px-3 py-2.5"
                        >
                          <div>
                            <div className="font-medium text-label">
                              {getAbsenceTypeLabel(day.effective_absence_type ?? "urlaub")}
                              <span className="ml-2 text-sm font-normal text-label-secondary">
                                · {fraction < 1 ? "0,5 Tage" : "1 Tag"}
                              </span>
                            </div>
                            <div className="text-sm text-label-secondary" title={calendarDayTitle(day)}>
                              {formatDateDe(day.work_date)} · {day.label}
                            </div>
                            <div className="mt-1 text-xs text-label-tertiary">
                              {calendarKindLabel(day.kind)} · automatisch genehmigt
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="rounded-full bg-status-green/15 px-2.5 py-1 text-xs font-semibold text-status-green">
                              {ABSENCE_STATUS_LABELS.approved}
                            </span>
                            {day.absence_request_status !== "pending" ? (
                              <div className="flex flex-wrap justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleRequestAutoAbsenceAlternative(day)}
                                >
                                  Abweichend
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => void handleWorkOnAutoAbsenceDay(day)}
                                >
                                  Arbeiten
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    }
                    const absence = item.absence;
                    return (
                      <div
                        key={item.key}
                        className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-violet-500/25 border-l-4 border-l-violet-500 bg-violet-500/5 px-3 py-2.5"
                      >
                        <div>
                          <div className="inline-flex items-center gap-1.5 font-medium text-label">
                            <HeroInlineLogo />
                            {getAbsenceTypeLabel(absence.absence_type)}
                          </div>
                          <div className="text-sm text-label-secondary">
                            {formatHeroAbsenceRange(absence)}
                          </div>
                          {absence.comment ? (
                            <div className="mt-1 text-sm text-label-secondary">{absence.comment}</div>
                          ) : null}
                        </div>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(absence.status)}`}
                        >
                          {ABSENCE_STATUS_LABELS[absence.status]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {timeline.some((item) => item.kind === "auto") ? (
          <p className="mt-4 text-xs text-label-tertiary">
            Brückentage und Betriebsferien sind automatisch genehmigt. „Abweichend“ oder „Arbeiten“
            gilt nur für diesen Tag. An Heiligabend und Silvester zählt ein halber Tag.
          </p>
        ) : null}
        {includeHeroOverlay && !heroLoading && !heroLinked ? (
          <p className="mt-3 text-xs text-label-tertiary">Kein Hero-Mitarbeiter verknüpft.</p>
        ) : null}
        {includeHeroOverlay && heroLinked ? (
          <p className="mt-3 text-xs text-label-tertiary">
            Violette Einträge kommen aus Hero und können nur dort geändert werden.
          </p>
        ) : null}
      </section>
      </div>

      <Dialog
        open={deleteTarget != null}
        onClose={closeOfficeDelete}
        size="sm"
        title="Abwesenheit löschen?"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeOfficeDelete} disabled={deleting}>
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={deleting}
              disabled={deleteReason.trim().length < 3}
              onClick={() => void confirmOfficeDelete()}
            >
              Mit Begründung löschen
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-ios border border-separator bg-card-elevated p-3">
              <div className="font-semibold text-label">
                {getAbsenceTypeLabel(deleteTarget.absence_type)} ·{" "}
                {formatAbsenceDays(deleteTarget.total_days)}
              </div>
              <div className="text-label">{formatAbsenceDateRange(deleteTarget)}</div>
            </div>
            <TextareaInput
              label="Begründung (Pflicht)"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Warum wird diese Abwesenheit gelöscht?"
              rows={3}
              required
            />
            <p className="text-xs text-label-secondary">
              Der Eintrag verschwindet aus Montageplan und Jahresübersicht. Die Begründung bleibt
              gespeichert.
            </p>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
